import {
  IGRNDetailProductInfoResponse,
  IGRNUpdatePendingResponseData,
  IGRNUpdatePutBody,
  IPOInvoiceOcrItem,
} from "@/api_framework/api_modals/Inventory";
import GRNStatusModal, { IGRNStatusModalState } from "@/app/components/routes/modals/GRNStatusModal";
import AlertBlue from "@/app/components/ui/Alerts/Blue";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import FormCheckbox from "@/app/components/ui/CheckBoxes/FormCheckbox";
import Input from "@/app/components/ui/Inputs/Input";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { useGrnDetailAPI } from "@/app/hooks/api_hooks/Inventory/PO/useGrnDetailAPI";
import { useGrnShortReceiveReasonAPI } from "@/app/hooks/api_hooks/Inventory/PO/useGrnShortReceiveReasonAPI";
import { useGrnUpdateAPI } from "@/app/hooks/api_hooks/Inventory/PO/useGrnUpdateApi";
import { useInventoryManufacturereListAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryManufacturereListAPI";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import { useUtils } from "@/app/hooks/useUtils";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import {
  IInventoryState,
  setActiveGRNId,
  setActivePOId,
  setActivePONumber,
  setPOGRNDetails,
} from "@/app_redux/reducers/slices/auth/inventory_slice";
import { addMonths, format } from "date-fns";
import { toNumber } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import GoodsReceiptNoteHeaderComponent from "../GoodsReceiptNoteHeaderComponent";
import RBACFlag from "@/app/components/core/RBACFlag";
export interface IPendingGRNFormFields extends IGRNDetailProductInfoResponse {
  is_selected: boolean;
}
export interface IGoodsReceiptNotePendingGoodsFormFields {
  pending_grn: IPendingGRNFormFields[];
}

const GoodsReceiptNotePendingGoodsComponent = () => {
  const { getRouteKey } = useRouter();
  const { setVisible, setModalData } = useModalActions("GRN_STATUS_UPDATE");
  const { getCurrentDate } = useUtils();
  const { forAlphaNumeric, forNumericWithDecimal, forOnlyNumberWithoutZero } = useFormValidations();
  const stringConstants = useStringConstants();
  const isGrnDetailInProgess = useLoader("isInventoryGrnDetailInProgress");

  const {
    shortReceiveReasonList,
    activePOId,
    transferOrderGRNId,
    activeGRNId,
    activePONumber,
    GRNDetails,
    purchaseOrderDetails: PODetails,
  } = useRouteInfo(getRouteKey("INVENTORY", "id")).routeState.state as IInventoryState;

  const fetchManufacturers = useInventoryManufacturereListAPI();
  const fetchShortReceiveReasonListAPI = useGrnShortReceiveReasonAPI();
  const dispatch = useAppDispatch();
  const { execute: updatePendingGRN } = useGrnUpdateAPI();
  const [params, _setParams] = useSearchParams();
  const { execute: fetchGrnDetail } = useGrnDetailAPI();
  const { toDetails } = useRouteInfo("INVENTORY")?.routeState?.state as IInventoryState;
  const to_id = params?.get("to_id");
  const po_id = params?.get("po_id");
  const [_data, setData] = useState([]);
  const shortReceivedReasonsItems = useSelectMenuReducer(shortReceiveReasonList, "reason_name", "reason_id");

  const formHook = useForm<IGoodsReceiptNotePendingGoodsFormFields>({
    mode: "onChange",
    defaultValues: { pending_grn: [] },
  });

  const pendingGRNFormHook = useFieldArray<IGoodsReceiptNotePendingGoodsFormFields>({
    control: formHook.control,
    name: "pending_grn",
  });

  useEffect(() => {
    if (po_id) {
      fetchGrnDetail(po_id);
    }
  }, [po_id]);

  useEffect(() => {
    if (to_id) {
      fetchGrnDetail(to_id, true);
    }
  }, [to_id]);

  useEffect(() => {
    if (activePOId) {
      fetchShortReceiveReasonListAPI();
      fetchManufacturers();
    }
  }, [activePOId]);

  useEffect(() => {
    if (GRNDetails) {
      setData([GRNDetails]);
    }
  }, [GRNDetails]);

  /** Invoice lines from the PO's scan, keyed by variant — the source of the lot/MRP/expiry prefill. */
  const ocrItemsByVariantId = useMemo(() => {
    const map = new Map<string, IPOInvoiceOcrItem>();
    (GRNDetails?.invoice_details?.ocr?.analysis?.items ?? []).forEach((item) => {
      if (item?.variant_id && !map.has(item.variant_id)) map.set(item.variant_id, item);
    });
    return map;
  }, [GRNDetails?.invoice_details?.ocr?.analysis?.items]);

  /**
   * `invoice_details.ocr` is read-only scan output the server attaches to the GRN. Both payloads
   * spread GRNDetails wholesale, so strip it here rather than posting the analysis back on every save.
   */
  const grnDetailsForPayload = useMemo(() => {
    if (!GRNDetails) return null;
    const { invoice_details: invoiceDetails, ...restOfGRNDetails } = GRNDetails;
    if (!invoiceDetails) return restOfGRNDetails;
    const { ocr: _ocrAnalysis, ...invoiceDetailsWithoutOcr } = invoiceDetails;
    return { ...restOfGRNDetails, invoice_details: invoiceDetailsWithoutOcr };
  }, [GRNDetails]);

  const constructGRNUpdateBody = (formData: IGoodsReceiptNotePendingGoodsFormFields): IGRNUpdatePutBody => {
    const selectedPendingGRNs = formData?.pending_grn
      .filter((pg) => pg?.is_selected === true)
      ?.map((grn) => {
        return {
          ...grn,
          received_packages: Number(grn?.received_packages) || 0,
          mrp: Number(grn?.mrp) || 0,
          package_count: Number(grn?.package_count) || 0,
          variant_name: grn?.variant_name,
          variant_id: grn?.variant_id,
        };
      });
    return {
      ...grnDetailsForPayload,
      po_id: activePOId,
      grn_id: activeGRNId,
      po_number: activePONumber,
      pending_grn: selectedPendingGRNs as IGRNUpdatePendingResponseData[],
    };
  };

  const constructGRNToPayload = (formData: IGoodsReceiptNotePendingGoodsFormFields): IGRNUpdatePutBody => {
    const selectedToPendingGRNs = formData?.pending_grn
      .filter((pg) => pg?.is_selected === true)
      ?.map((item) => {
        return {
          ...item,
          pending_packages: item?.pending_packages,
          received_packages: Number(item?.received_packages) || 0,
          mrp: Number(item?.mrp) || 0,
          transfer_qty: Number(item?.transfer_qty) || 0,
          variant_name: item?.variant_name,
          variant_id: item?.variant_id,
          unique_row_id: item?.unique_row_id,
        };
      });
    return {
      ...grnDetailsForPayload,
      grn_id: transferOrderGRNId ? transferOrderGRNId : activeGRNId,
      pending_grn: selectedToPendingGRNs as IGRNUpdatePendingResponseData[],
      transfer_order_number: toDetails?.transfer_order_number,
      transfer_order_id: toDetails?.transfer_order_id,
      transfer_order_clinic_details: toDetails?.transfer_order_clinic_details,
    };
  };

  const onSubmit = async () => {
    const formData = formHook.getValues();

    const selectedIndexes = formData.pending_grn
      .map((row, i) => (row.is_selected ? i : null))
      .filter((i) => i !== null) as number[];

    const fieldsToValidate = selectedIndexes.flatMap((i) => [
      `pending_grn.${i}.received_packages`,
      `pending_grn.${i}.lot_number`,
      `pending_grn.${i}.expiry_date`,
      `pending_grn.${i}.mrp`,
      `pending_grn.${i}.reason_for_short_receive`,
    ]);

    const isValid = await formHook.trigger(fieldsToValidate as any);

    if (!isValid) {
      toast.error("LOT Number, Manfacturer, Expiry Date, MRP and Reason for Short Receive are required.");
      throw new Error("Form invalid");
    }
    const GRNUpdatePayload = constructGRNUpdateBody(formData);
    const GRNToPayload = constructGRNToPayload(formData);

    if (GRNUpdatePayload?.pending_grn?.length === 0) {
      toast.error("Please select atleast one product for GRN.");
      throw new Error("No product selected");
    }

    const res = await updatePendingGRN(to_id ? GRNToPayload : GRNUpdatePayload, activePOId || po_id || to_id, !!to_id);

    if (res?.status) {
      dispatch(setPOGRNDetails(null));

      window.location.href = `${getRouteKey("GRN_DONE", "url")}${to_id ? `?to_id=${to_id}` : `?po_id=${activePOId}`}`;
    }
  };

  useEffect(() => {
    if (GRNDetails?.pending_grn?.length === 0) {
      formHook.setValue("pending_grn", []);
    }
    if (GRNDetails?.pending_grn?.length > 0) {
      dispatch(setActiveGRNId(GRNDetails?.grn_id));
      dispatch(setActivePOId(GRNDetails?.po_id));
      dispatch(setActivePONumber(GRNDetails?.po_number));
      formHook.resetField("pending_grn");
      formHook.setValue(
        "pending_grn",
        GRNDetails?.pending_grn?.map((pg) => {
          // The scan only fills a field the GRN has not got yet — a value already on the row wins.
          const ocrItem = ocrItemsByVariantId.get(pg?.variant_id);
          const scannedMrp = ocrItem?.mrp !== null && ocrItem?.mrp !== undefined ? String(ocrItem.mrp) : "";
          return {
            ...pg,
            is_selected: false,
            expiry_date: pg?.expiry_date || ocrItem?.expiry_date || (to_id ? getCurrentDate() : ""),
            mrp: pg?.mrp || scannedMrp || undefined,
            lot_number: pg?.lot_number || ocrItem?.lot_number || undefined,
          };
        }),
      );
    }
  }, [GRNDetails?.pending_grn, ocrItemsByVariantId]);

  const updatePoPendingPackages = (rowIndex: number) => {
    const packagePoCount = formHook.getValues(`pending_grn.${rowIndex}.package_count`);
    const receivedPoPackages = formHook.getValues(`pending_grn.${rowIndex}.received_packages`);
    const pendingPoPackages = Math.abs(+packagePoCount - +receivedPoPackages);
    formHook.setValue(`pending_grn.${rowIndex}.pending_packages`, pendingPoPackages);
  };

  const updateToPendingPackages = (rowIndex: number) => {
    const packageToCount = formHook.getValues(`pending_grn.${rowIndex}.transfer_qty`);
    const receivedToPackages = formHook.getValues(`pending_grn.${rowIndex}.received_packages`);
    const pendingToPackages = Math.abs(+packageToCount - +receivedToPackages);
    formHook.setValue(`pending_grn.${rowIndex}.pending_packages`, pendingToPackages);
  };

  return (
    <div className="bg-white px-3 py-3 p-4 rounded-md">
      <GRNStatusModal
        onSubmit={async () => {
          await onSubmit();
        }}
      />
      <GoodsReceiptNoteHeaderComponent />
      <form className="mt-2 w-full" onSubmit={formHook.handleSubmit(onSubmit)}>
        <h3 className="mt-5 px-2 py-3 font-medium text-[10px] sm:text-xs 2xl:text-sm">Listed Product Names</h3>
        {isGrnDetailInProgess ? (
          <TableSkeleton />
        ) : (
          <div className="shadow-md overflow-scroll">
            {pendingGRNFormHook && pendingGRNFormHook?.fields?.length > 0 ? (
              <table className="min-w-max table-auto border-collapse">
                <thead>
                  <tr className="bg-gray-200 text-left text-[10px] sm:text-xs 2xl:text-sm font-medium sticky top-0 z-20">
                    <th className="sticky top-0 left-0 z-20 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2 bg-gray-300">
                      Product Name
                    </th>
                    {po_id && (
                      <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                        Pkgs to be received
                      </th>
                    )}
                    {to_id && (
                      <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                        Qty to be received
                      </th>
                    )}
                    {po_id && (
                      <>
                        <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                          Ordered Packages
                        </th>
                        <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                          Qty/Pkg
                        </th>
                      </>
                    )}
                    <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                      Received Pkgs
                    </th>
                    <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                      Pending Pkgs
                    </th>
                    {po_id && (
                      <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                        Reason for Short Receive
                      </th>
                    )}
                    <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                      Lot No.
                    </th>
                    <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                      Manufacturer
                    </th>
                    <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                      Exp Date
                    </th>
                    <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                      MRP (Tax inclusive)
                    </th>
                    {po_id && (
                      <>
                        {" "}
                        <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                          Cost/Pkg
                        </th>
                        <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                          Discount
                        </th>
                        <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                          Total Tax
                        </th>
                        <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                          Total Cost (inclusive of discount & Tax)
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-orange-100">
                  {pendingGRNFormHook?.fields?.map((field, index) => {
                    return (
                      <tr key={field?.id || field?.product_id}>
                        <td className={"px-3 py-4 text-xs font-normal text-left left-0 sticky z-20 bg-gray-50"}>
                          <div className="flex items-center">
                            {PODetails?.po_status === "closed" || PODetails?.po_status === "issue_in_grn" ? null : (
                              <FormCheckbox
                                colorHash="#22c55e"
                                fieldError={
                                  formHook?.formState?.errors?.pending_grn
                                    ? formHook?.formState?.errors?.pending_grn?.[index]
                                      ? formHook?.formState?.errors?.pending_grn?.[index]?.is_selected
                                        ? formHook?.formState?.errors?.pending_grn?.[index]?.is_selected
                                        : null
                                      : null
                                    : null
                                }
                                register={formHook.register(`pending_grn.${index}.is_selected`)}
                                defaultChecked={formHook.watch(`pending_grn.${index}.is_selected`)}
                                errorMessages={[]}
                                onCheckChange={(checked) => {
                                  formHook.setValue(`pending_grn.${index}.is_selected`, checked);
                                }}
                              />
                            )}{" "}
                            <p className="break-words w-52">{field?.variant_name || field?.product_name}</p>
                          </div>
                        </td>
                        <td className={"px-6 py-4 whitespace-nowrap text-xs font-normal text-left"}>
                          <p>{field?.transfer_qty || field?.package_count}</p>
                        </td>
                        {po_id && (
                          <>
                            <td className={"px-6 py-4 whitespace-nowrap text-xs font-normal text-left"}>
                              <p>{field?.ordered_packages ?? "Not Available"}</p>
                            </td>
                            <td className={"px-6 py-4 whitespace-nowrap text-xs font-normal text-left"}>
                              <p>{field?.qty_per_package}</p>
                            </td>
                          </>
                        )}
                        {to_id && (
                          <td className={"px-3 py-4 text-xs font-normal"}>
                            <div className="pt-2 pb-2 w-full">
                              <Input
                                disabled={to_id && !formHook.watch(`pending_grn.${index}.is_selected`)}
                                errorMessages={[{ message: "", type: "required" }, forOnlyNumberWithoutZero.errors]}
                                fieldError={
                                  formHook?.formState?.errors?.pending_grn
                                    ? formHook?.formState?.errors?.pending_grn?.[index]
                                      ? formHook?.formState?.errors?.pending_grn?.[index]?.received_packages
                                        ? formHook?.formState?.errors?.pending_grn?.[index]?.received_packages
                                        : null
                                      : null
                                    : null
                                }
                                register={formHook.register(`pending_grn.${index}.received_packages`, {
                                  required: formHook.watch(`pending_grn.${index}.is_selected`),
                                  onChange: (e) => {
                                    if (e.target.value > field?.transfer_qty) {
                                      formHook.setValue(`pending_grn.${index}.received_packages`, field?.transfer_qty);
                                      updateToPendingPackages(index);
                                    } else {
                                      updateToPendingPackages(index);
                                    }
                                    formHook.clearErrors(`pending_grn.${index}.received_packages`);
                                  },
                                  ...forOnlyNumberWithoutZero.validations,
                                })}
                                type="number"
                                max={field?.transfer_qty}
                                min={0}
                              />
                            </div>
                          </td>
                        )}
                        {po_id && (
                          <td className={"px-3 py-4 text-xs font-normal"}>
                            <div className="pt-2 pb-2 w-full">
                              <Input
                                disabled={po_id && !formHook.watch(`pending_grn.${index}.is_selected`)}
                                errorMessages={[{ message: "", type: "required" }, forOnlyNumberWithoutZero.errors]}
                                fieldError={
                                  formHook?.formState?.errors?.pending_grn
                                    ? formHook?.formState?.errors?.pending_grn?.[index]
                                      ? formHook?.formState?.errors?.pending_grn?.[index]?.received_packages
                                        ? formHook?.formState?.errors?.pending_grn?.[index]?.received_packages
                                        : null
                                      : null
                                    : null
                                }
                                register={formHook.register(`pending_grn.${index}.received_packages`, {
                                  required: formHook.watch(`pending_grn.${index}.is_selected`),
                                  onChange: (e) => {
                                    if (e.target.value > field?.package_count) {
                                      formHook.setValue(`pending_grn.${index}.received_packages`, field?.package_count);
                                      updatePoPendingPackages(index);
                                    } else {
                                      updatePoPendingPackages(index);
                                    }
                                    formHook.clearErrors(`pending_grn.${index}.received_packages`);
                                  },
                                  ...forOnlyNumberWithoutZero.validations,
                                })}
                                type="number"
                                max={field?.package_count}
                                min={0}
                              />
                            </div>
                          </td>
                        )}
                        <td className={`px-6 py-4 whitespace-nowrap text-xs font-normal text-left`}>
                          <p>{formHook.getValues(`pending_grn.${index}.pending_packages`)}</p>
                        </td>
                        {po_id && (
                          <td className={`px-3 py-4 text-xs font-normal`}>
                            <SearchableSelectMenu
                              disabled={
                                !formHook.watch(`pending_grn.${index}.is_selected`) ||
                                toNumber(field?.package_count) ===
                                  toNumber(formHook.watch(`pending_grn.${index}.received_packages`))
                              }
                              errorMessages={[]}
                              onSelectItem={(item) => {
                                if (item) {
                                  formHook.setValue(`pending_grn.${index}.reason_for_short_receive`, item?.title);
                                  formHook.clearErrors(`pending_grn.${index}.reason_for_short_receive`);
                                }
                              }}
                              fieldError={
                                formHook?.formState?.errors?.pending_grn
                                  ? formHook?.formState?.errors?.pending_grn?.[index]
                                    ? formHook?.formState?.errors?.pending_grn?.[index]?.reason_for_short_receive
                                      ? formHook?.formState?.errors?.pending_grn?.[index]?.reason_for_short_receive
                                      : null
                                    : null
                                  : null
                              }
                              register={formHook.register(`pending_grn.${index}.reason_for_short_receive`, {
                                validate: (value) => {
                                  const pkgCount = toNumber(field?.package_count);
                                  const received = toNumber(formHook.watch(`pending_grn.${index}.received_packages`));
                                  const pending = pkgCount - received;

                                  // If there are pending packages, reason is required
                                  if (pending > 0 && !value) {
                                    return "Reason for short receive is required when there are pending packages.";
                                  }

                                  return true;
                                },
                              })}
                              selectItems={shortReceivedReasonsItems}
                              defaultSelected={
                                shortReceivedReasonsItems?.filter(
                                  (i) => i.title === formHook.watch(`pending_grn.${index}.reason_for_short_receive`),
                                )[0]
                              }
                              placeholder="Select Reason"
                              showTooltips={true}
                              showTypedErrors={true}
                              showDropdownIcon={true}
                              className="text-gray-800 w-32"
                              containerClassName="w-full"
                            />
                            <p className="whitespace-break-spaces text-[10px] mt-1 w-11/12">
                              {formHook.watch(`pending_grn.${index}.reason_for_short_receive`)}
                            </p>
                          </td>
                        )}
                        {po_id && (
                          <>
                            <td className={`px-3 py-4 whitespace-nowrap text-xs font-normal`}>
                              <div className="pt-2 pb-2 w-full">
                                <Input
                                  disabled={!formHook.watch(`pending_grn.${index}.is_selected`)}
                                  errorMessages={[{ type: "required", message: "" }, forAlphaNumeric.errors]}
                                  fieldError={
                                    formHook?.formState?.errors?.pending_grn
                                      ? formHook?.formState?.errors?.pending_grn?.[index]
                                        ? formHook?.formState?.errors?.pending_grn?.[index]?.lot_number
                                          ? formHook?.formState?.errors?.pending_grn?.[index]?.lot_number
                                          : null
                                        : null
                                      : null
                                  }
                                  defaultValue={field?.lot_number}
                                  register={formHook.register(`pending_grn.${index}.lot_number`, {
                                    required: formHook.watch(`pending_grn.${index}.is_selected`),
                                    ...forAlphaNumeric.validations,
                                    onChange: () => {
                                      formHook.clearErrors(`pending_grn.${index}.lot_number`);
                                    },
                                  })}
                                />
                              </div>
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-xs font-normal `}>
                              {field?.manufacturer?.manufacturer_name ?? "Not Available"}
                            </td>
                            <td className="py-3">
                              {PODetails?.po_status === "closed" || PODetails?.po_status === "issue_in_grn" ? (
                                <span className="text-xs">
                                  {formHook.getValues(`pending_grn.${index}.expiry_date`)}
                                </span>
                              ) : (
                                <PWLCalendarComponent
                                  disabled={!!to_id}
                                  disablePastMonthSelection
                                  errorMessages={[{ message: "", type: "required" }]}
                                  register={formHook.register(`pending_grn.${index}.expiry_date`, {
                                    required: true,
                                  })}
                                  disableDatesBeforeDate={format(addMonths(new Date(), 3), "yyyy-MM-dd")}
                                  fieldError={
                                    formHook?.formState?.errors?.pending_grn
                                      ? formHook?.formState?.errors?.pending_grn?.[index]
                                        ? formHook?.formState?.errors?.pending_grn?.[index]?.expiry_date
                                          ? formHook?.formState?.errors?.pending_grn?.[index]?.expiry_date
                                          : null
                                        : null
                                      : null
                                  }
                                  allowPastDaySelection={false}
                                  showFutureYears={true}
                                  onSelectDate={(date: Date) => {
                                    const formattedDate = format(date, stringConstants?.fieldDOBFormat);
                                    formHook.setValue(`pending_grn.${index}.expiry_date`, formattedDate);
                                    formHook.clearErrors(`pending_grn.${index}.expiry_date`);
                                  }}
                                  defaultSelectedDate={
                                    formHook.getValues(`pending_grn.${index}.expiry_date`) || field?.expiry_date
                                  }
                                  className="w-full"
                                />
                              )}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-xs font-normal`}>
                              <div className="pt-2 pb-2 w-20">
                                <Input
                                  disabled={!formHook.watch(`pending_grn.${index}.is_selected`)}
                                  errorMessages={[{ type: "required", message: "" }, forNumericWithDecimal.errors]}
                                  fieldError={
                                    formHook?.formState?.errors?.pending_grn
                                      ? formHook?.formState?.errors?.pending_grn?.[index]
                                        ? formHook?.formState?.errors?.pending_grn?.[index]
                                          ? formHook?.formState?.errors?.pending_grn?.[index]?.mrp
                                            ? formHook?.formState?.errors?.pending_grn?.[index]?.mrp
                                            : null
                                          : null
                                        : null
                                      : null
                                  }
                                  defaultValue={field?.mrp}
                                  register={formHook.register(`pending_grn.${index}.mrp`, {
                                    required: formHook.watch(`pending_grn.${index}.is_selected`),
                                    ...forNumericWithDecimal.validations,
                                    onChange: () => {
                                      formHook.clearErrors(`pending_grn.${index}.mrp`);
                                    },
                                  })}
                                  type="number"
                                  min={1}
                                />
                              </div>
                            </td>
                          </>
                        )}
                        {to_id && (
                          <>
                            <td className="px-6 py-4 font-normal text-xs whitespace-nowrap">{field?.lot_number}</td>
                            <td className="px-6 py-4 font-normal text-xs whitespace-nowrap">
                              {field?.manufacturer_name}
                            </td>
                            <td className="px-6 py-4 font-normal text-xs whitespace-nowrap">{field?.expiry_date}</td>
                            <td className="px-6 py-4 font-normal text-xs whitespace-nowrap">{field?.mrp}</td>
                          </>
                        )}
                        {po_id && (
                          <>
                            <td className={`px-6 py-4 whitespace-nowrap text-xs font-normal text-center `}>
                              <p>{field?.price_per_package}</p>
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-xs font-normal text-center `}>
                              <p>{field?.discount_percentage}%</p>
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-xs font-normal text-center `}>
                              <p>{field?.tax_details}</p>
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-xs font-normal text-center `}>
                              <p>{field?.total_cost_after_tax}</p>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <AlertBlue title="All Products / Goods have been moved to GRN Done" />
            )}
          </div>
        )}
        <RBACFlag actionId="RECEIVE_GOODS_ACTION_CTA">
          {!(PODetails?.po_status === "closed" || PODetails?.po_status === "issue_in_grn") &&
            pendingGRNFormHook?.fields?.length > 0 && (
              <div className="bottom-0 flex justify-end gap-4 mt-5 p-4">
                <button
                  type="button"
                  onClick={() => {
                    setModalData({
                      GRNUpdatePayload: constructGRNUpdateBody(formHook.getValues()),
                      GRNToPayload: constructGRNToPayload(formHook.getValues()),
                    } as IGRNStatusModalState);
                    setVisible();
                  }}
                  className="bg-[#193A6A] px-2.5 py-1.5 rounded-md font-normal text-[10px] sm:text-xs 2xl:text-sm text-white cursor-pointer"
                >
                  Receive Goods
                </button>
              </div>
            )}
        </RBACFlag>
      </form>
    </div>
  );
};

export default GoodsReceiptNotePendingGoodsComponent;
